import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography, Grid } from "@mui/material";
import AppLoading from "verticales/AppLoading.tsx";
import TableMaestros from "../../../verticales/TableMaestrosInformes.tsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

const CircularProgressWithLabel = ({ value }) => {
  const getColor = (value) => {
    if (value < 10) return "error.main";
    if (value > 10 && value <= 45) return "warning.main";
    if (value > 45 && value <= 100) return "success.main";
    return "error.main";
  };

  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress variant="determinate" value={value} sx={{ color: getColor(value) }} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="caption" component="div" color="text.secondary">
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

CircularProgressWithLabel.propTypes = {
  value: PropTypes.number.isRequired,
};

// Genera el estado con MDBadge interactivo
const getEstadoBadge = (details, onClick) => {
  let color = "success";
  let label = "OK";

  for (const disco of details) {
    if (disco.free_percent < 10) {
      color = "error";
      label = "Crítico";
      break;
    } else if (disco.free_percent <= 45 && color !== "error") {
      color = "warning";
      label = "Alerta";
    }
  }

  return (
    <MDBadge
      badgeContent={label}
      color={color}
      variant="gradient"
      size="sm"
      onClick={() => onClick(label)}
      sx={{
        cursor: "pointer",
        minWidth: 70,
        justifyContent: "center",
        display: "flex",
        textAlign: "center",
        paddingX: "6px",
      }}
    />
  );
};

const ViewDiscos = ({ data = [], loading = false }) => {
  const [loadingState, setLoadingState] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [estadoFilter, setEstadoFilter] = useState(null); // Filtro interactivo

  const groupDataByInstanceId = (data) => {
    const groupedData = data.reduce((acc, item) => {
      const { instance_id } = item;

      if (instance_id?.startsWith("mi-") && !item.cuenta) {
        item.cuenta = "claro";
      }

      if (!acc[instance_id]) {
        acc[instance_id] = {
          ...item,
          details: [
            {
              disco: item.disk,
              tamano: item.total_size,
              used_space: item.used_space,
              free_space: item.free_space,
              free_percent: item.free_percent,
            },
          ],
        };
      } else {
        acc[instance_id].details.push({
          disco: item.disk,
          tamano: item.total_size,
          used_space: item.used_space,
          free_space: item.free_space,
          free_percent: item.free_percent,
        });
      }

      acc[instance_id].details = acc[instance_id].details.filter(
        (value, index, self) => index === self.findIndex((t) => t.disco === value.disco)
      );

      return acc;
    }, {});

    return Object.values(groupedData);
  };

  const loadData = async () => {
    setLoadingState(true);
    try {
      const grouped = groupDataByInstanceId(data);
      setTableData(grouped);
    } catch (error) {
      console.error("Error al procesar datos:", error);
    } finally {
      setLoadingState(false);
    }
  };

  const filteredData = useMemo(() => {
    if (!estadoFilter) return tableData;

    return tableData.filter((item) => {
      const estado = item.details.some((d) => d.free_percent < 10)
        ? "Crítico"
        : item.details.some((d) => d.free_percent <= 45)
        ? "Alerta"
        : "OK";
      return estado === estadoFilter;
    });
  }, [tableData, estadoFilter]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "cuenta",
        header: "Cuenta",
        size: 150,
        filterVariant: "multi-select",
        filterSelectOptions: tableData
          .map((item) => item.cuenta)
          .filter((value, index, self) => self.indexOf(value) === index),
      },
      { accessorKey: "instance_id", header: "Instance ID", size: 200 },
      { accessorKey: "instance_name", header: "Instance Name", size: 200, enableGrouping: false },
      { accessorKey: "ip_privada", header: "IP Privada", size: 200 },
      { accessorKey: "ip_publica", header: "IP Pública", size: 200 },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 120,
        Cell: ({ row }) =>
          getEstadoBadge(row.original.details, (estado) => {
            setEstadoFilter((prev) => (prev === estado ? null : estado));
          }),
      },
    ],
    [tableData, estadoFilter]
  );

  const handleExportData = () => {
    console.log("Exportar datos");
  };

  useEffect(() => {
    if (data.length > 0) {
      loadData();
    } else {
      console.warn("No se recibieron datos iniciales");
      setLoadingState(true);
    }
  }, [data]);

  return (
    <Box sx={{ marginTop: "1px" }}>
      {loadingState ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <AppLoading titleCustom={"EC2 Discos"} />
        </Box>
      ) : (
        <TableMaestros
          height={250}
          arrayColumns={columns}
          data={filteredData}
          renderDetailPanel={(row) => (
            <Box sx={{ padding: "16px" }}>
              <Typography variant="h6" sx={{ marginBottom: "8px" }}>
                Detalle de Discos - {row.original.instance_name || "Sin Nombre"}
              </Typography>
              <Grid container>
                {row.original.details.map((entry, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={3}>
                      <Typography variant="body2">Disco:</Typography>
                      <Typography variant="caption">{entry.disco}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body2">Tamaño:</Typography>
                      <Typography variant="caption">{entry.tamano} GB</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body2">Espacio Usado:</Typography>
                      <Typography variant="caption">{entry.used_space} GB</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2">Porcentaje Libre:</Typography>
                      <CircularProgressWithLabel value={entry.free_percent} />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          )}
          enableGrouping
          enableStickyHeader
          enableColumnResizing
          muiTableContainerProps={{
            sx: { maxHeight: "calc(100vh - 445px)", minHeight: "150px" },
          }}
          muiTableBodyCellProps={{
            sx: {
              borderRight: "1px solid #e0e0e0",
              fontSize: 14,
              padding: "0.5rem",
            },
          }}
          muiTableHeadCellProps={{
            sx: (theme) => ({
              backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#303030",
              fontSize: "14px",
              fontWeight: "bold",
            }),
          }}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", padding: "4px" }}>
              <MDButton
                onClick={handleExportData}
                startIcon={<FileDownloadIcon />}
                sx={{ fontSize: "12px", fontWeight: "normal" }}
              >
                Exportar Datos
              </MDButton>

              {estadoFilter && (
                <MDButton
                  onClick={() => setEstadoFilter(null)}
                  color="info"
                  variant="outlined"
                  sx={{ fontSize: "12px", fontWeight: "normal" }}
                >
                  Limpiar filtro de estado
                </MDButton>
              )}
            </Box>
          )}
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 50 },
          }}
        />
      )}
    </Box>
  );
};

ViewDiscos.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      cuenta: PropTypes.string,
      instance_id: PropTypes.string.isRequired,
      instance_name: PropTypes.string,
      ip_privada: PropTypes.string,
      ip_publica: PropTypes.string,
      disk: PropTypes.string,
      total_size: PropTypes.number,
      used_space: PropTypes.number,
      free_space: PropTypes.number,
      free_percent: PropTypes.number,
    })
  ),
  loading: PropTypes.bool,
};

export default ViewDiscos;
